import { useId, type SVGProps } from 'react';

const SYMBOL =
  'M76.2748 0.674805C82.2395 0.674805 87.0748 5.51015 87.0748 11.4748C87.0748 13.7257 86.3862 15.8156 85.2081 17.5455C82.4737 21.5608 78.1349 24.9073 76.9566 29.6202C75.5453 35.2654 78.5415 40.998 81.3129 46.1146C82.6918 48.6604 83.4748 51.5761 83.4748 54.6748C83.4748 64.6159 75.4159 72.6748 65.4748 72.6748C55.5337 72.6748 47.4748 64.6159 47.4748 54.6748C47.4748 54.6182 47.4751 54.5617 47.4756 54.5052C47.5066 51.1531 46.5614 47.6343 43.8577 45.6525C41.0043 43.561 37.1391 43.8115 33.9313 45.3035C30.9358 46.6969 27.5962 47.4748 24.0748 47.4748C11.1513 47.4748 0.674805 36.9983 0.674805 24.0748C0.674834 11.1514 11.1514 0.674805 24.0748 0.674805C33.2809 0.674805 41.2449 5.99129 45.0651 13.7213L52.6379 28.3775C53.7717 30.5718 56.0352 31.95 58.5051 31.95C62.0851 31.95 65.0132 29.0976 65.1069 25.5189L65.4748 11.475C65.4748 5.51037 70.3102 0.674805 76.2748 0.674805ZM65.4748 45.6748C60.5043 45.6748 56.4748 49.7043 56.4748 54.6748C56.4748 59.6454 60.5042 63.6748 65.4748 63.6748C70.4454 63.6748 74.4748 59.6454 74.4748 54.6748C74.4748 49.7043 70.4454 45.6748 65.4748 45.6748ZM24.0748 11.4748C16.9918 11.4748 11.2498 17.2168 11.2498 24.2998C11.2498 31.3829 16.9918 37.1248 24.0748 37.1248C31.1579 37.1248 36.8998 31.3829 36.8998 24.2998C36.8998 17.2168 31.1578 11.4748 24.0748 11.4748ZM76.2748 6.0748C73.2925 6.0748 70.8748 8.49249 70.8748 11.4748C70.8748 14.4571 73.2925 16.8748 76.2748 16.8748C79.2571 16.8748 81.6748 14.4571 81.6748 11.4748C81.6748 8.49249 79.2571 6.0748 76.2748 6.0748Z';
const WORDMARK =
  'M133.152 61.1748L113.654 12.0812H121.79L136.869 51.5665L152.579 12.0812H160.223L140.235 61.1748H133.152ZM162.986 61.1748V12.0812H187.392V18.6036H170.35V31.9992H186.621V38.3112H170.35V54.6524H188.094V61.1748H162.986ZM193.242 61.1748V12.0812H206.076C215.054 12.0812 221.436 17.8321 221.436 25.6871C221.436 32.1394 217.087 37.0488 210.425 38.5917L226.345 61.1748H217.649L200.606 36.4877V61.1748H193.242ZM200.606 33.3317H205.866C210.635 33.3317 214.072 30.3159 214.072 25.8274C214.072 21.3388 210.705 18.3231 205.866 18.3231H200.606V33.3317ZM229.32 61.1748V12.0812H236.263L263.896 48.8313V12.0812H271.049V61.1748H264.176L236.473 24.4247V61.1748H229.32ZM278.342 61.1748V12.0812H302.749V18.6036H285.706V31.9992H301.977V38.3112H285.706V54.6524H303.45V61.1748H278.342Z';

type LogoProps = Omit<SVGProps<SVGSVGElement>, 'viewBox' | 'children'>;

function VerneSymbol() {
  const id = useId().replace(/[^\w-]/g, '');
  return (
    <>
      <mask id={`${id}-mask1`} fill="white">
        <path fillRule="evenodd" clipRule="evenodd" d={SYMBOL} />
      </mask>
      <g filter={`url(#${id}-shade)`}>
        <path fillRule="evenodd" clipRule="evenodd" d={SYMBOL} fill={`url(#${id}-fill)`} />
      </g>
      <path fillRule="evenodd" clipRule="evenodd" d={SYMBOL} stroke={`url(#${id}-edge1)`} strokeOpacity="0.08" strokeWidth="0.9" mask={`url(#${id}-mask1)`} />
      <g filter={`url(#${id}-blur)`} style={{ mixBlendMode: 'plus-lighter' }}>
        <mask id={`${id}-mask2`} fill="white">
          <path fillRule="evenodd" clipRule="evenodd" d={SYMBOL} />
        </mask>
        <path fillRule="evenodd" clipRule="evenodd" d={SYMBOL} stroke={`url(#${id}-edge2)`} strokeOpacity="0.14" strokeWidth="1.8" mask={`url(#${id}-mask2)`} />
      </g>
      <defs>
        <filter id={`${id}-shade`} x="-0.000195265" y="-0.000195265" width="87.7504" height="73.35" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
          <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
          <feOffset dx="-0.675" dy="-0.675" />
          <feGaussianBlur stdDeviation="0.45" />
          <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
          <feColorMatrix type="matrix" values="0 0 0 0 1 0 0 0 0 0.546667 0 0 0 0 0.2 0 0 0 0.15 0" />
          <feBlend mode="normal" in2="shape" result="effect1_innerShadow_997_142" />
          <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
          <feOffset dx="0.675" dy="0.675" />
          <feGaussianBlur stdDeviation="0.45" />
          <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
          <feColorMatrix type="matrix" values="0 0 0 0 1 0 0 0 0 0 0 0 0 0 0 0 0 0 0.18 0" />
          <feBlend mode="normal" in2="effect1_innerShadow_997_142" result="effect2_innerShadow_997_142" />
          <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
          <feOffset dx="-0.675" />
          <feGaussianBlur stdDeviation="1.125" />
          <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
          <feColorMatrix type="matrix" values="0 0 0 0 1 0 0 0 0 0.546667 0 0 0 0 0.2 0 0 0 0.14 0" />
          <feBlend mode="normal" in2="effect2_innerShadow_997_142" result="effect3_innerShadow_997_142" />
          <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
          <feOffset dx="0.675" />
          <feGaussianBlur stdDeviation="1.125" />
          <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
          <feColorMatrix type="matrix" values="0 0 0 0 1 0 0 0 0 0 0 0 0 0 0 0 0 0 0.12 0" />
          <feBlend mode="normal" in2="effect3_innerShadow_997_142" result="effect4_innerShadow_997_142" />
        </filter>
        <filter id={`${id}-blur`} x="-0.000195265" y="-0.000195265" width="87.7504" height="73.35" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
          <feGaussianBlur stdDeviation="0.3375" result="effect1_foregroundBlur_997_142" />
        </filter>
        <linearGradient id={`${id}-fill`} x1="43.8748" y1="0.674805" x2="43.8748" y2="72.6748" gradientUnits="userSpaceOnUse">
          <stop stopColor="#FF4502" />
          <stop offset="1" stopColor="#FF6933" />
        </linearGradient>
        <linearGradient id={`${id}-edge1`} x1="43.8748" y1="0.674805" x2="43.8748" y2="72.6748" gradientUnits="userSpaceOnUse">
          <stop stopColor="white" />
          <stop offset="0.5" stopColor="white" stopOpacity="0.3" />
          <stop offset="1" stopColor="white" />
        </linearGradient>
        <linearGradient id={`${id}-edge2`} x1="43.8748" y1="0.674805" x2="43.8748" y2="72.6748" gradientUnits="userSpaceOnUse">
          <stop stopColor="white" />
          <stop offset="0.5" stopColor="white" stopOpacity="0.3" />
          <stop offset="1" stopColor="white" />
        </linearGradient>
      </defs>
    </>
  );
}

/** Verne symbol and VERNE wordmark (Figma node 997:142). The wordmark takes `currentColor`. */
export function VerneLogo(props: LogoProps) {
  return (
    <svg viewBox="0 0 307 74" fill="none" role="img" aria-label="Verne" {...props}>
      <VerneSymbol />
      <path d={WORDMARK} fill="currentColor" />
    </svg>
  );
}

/** Verne symbol only. */
export function VerneMark(props: LogoProps) {
  return (
    <svg viewBox="0 0 88 74" fill="none" role="img" aria-label="Verne" {...props}>
      <VerneSymbol />
    </svg>
  );
}
